import React, { useEffect, useState } from 'react';
import type { ChangeEvent, KeyboardEventHandler } from 'react';
import {
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Input,
    InputGroup,
    InputRightElement,
    Link,
} from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { useDebounceValue } from 'usehooks-ts';
import {
    shouldQueryBloomreach,
    useGetBloomreachSearch,
    useGetSuggestedBrands,
} from '../../../../../bff-api/bloomreach-recommendation/useBloomreachSearch';
import { useCategories } from '../../../../../bff-api/categories/useCategories';
import { useCustomer } from '../../../../../contexts/useCustomer';
import { useABTestVariable } from '../../../../../hooks/useABTestVariable';
import { useBuildURL } from '../../../../../hooks/useBuildURL';
import { useNavigateTo } from '../../../../../hooks/useNavigateTo';
import { useSearchUrl } from '../../../../../hooks/useSearchUrl';
import { PageUrl } from '../../../../../pages/utils/page-url';
import { Text } from '../../../../../ui-components';
import { useCustomPreferenceOrDefault, useUrl } from '../../../../../utils/api';
import { CustomPreference } from '../../../../../utils/api/CustomPreference';
import { isBrandsCategory } from '../../../../../utils/category-utils';
import { isHydrated } from '../../../../../utils/utils';
import { sanitizeHtml } from '../../../../content-asset/sanitize-html';
import {
    ArrowLeftIcon,
    BrandLogo,
    CircleCloseIcon,
    ContactIcon,
    SearchIcon,
    StoreIcon,
    UserIcon,
} from '../../../../icons';
import { MenuSearchResults } from '../../menu-search/menu-search-results';
import styles from './MenuCategoryMobile.module.scss';
import { MenuCategoryBrandsMobile } from './menu-category-brands-mobile';
import { MenuCategoryMobileLevel12 } from './menu-category-mobile-level-1-2';
import { MenuCategoryMobileLevel34 } from './menu-category-mobile-level-3-4';
import type { Categories, Category } from '../../../../../bff-api/categories/model/category';

enum CategoryLevel {
    ONE = 1,
    TWO,
    THREE,
    FOUR,
}

const BannerLayout = ({ id, banner }: { id: string; banner: string }): React.ReactElement => (
    <Flex
        data-testid={id}
        direction="column"
        rowGap={4}
        textStyle="body-base"
        paddingTop={8}
        paddingBottom={4}
        paddingX={4}
        className={styles.menu_banner}
        dangerouslySetInnerHTML={{
            __html: sanitizeHtml(banner),
        }}
    />
);

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export const MenuCategoryMobile: React.FC<Props> = ({ isOpen, onClose }) => {
    const intl = useIntl();
    const { data } = useCategories();
    const { isRegistered, isInitialized, customer } = useCustomer();
    const accountUrl = useUrl(PageUrl.ACCOUNT_SHOW);
    const enableRioSEO = useCustomPreferenceOrDefault(CustomPreference.ENABLE_RIO_SEO, true);
    const pwaSearchMode = useABTestVariable<string>(CustomPreference.PWA_SEARCH_MODE, 'SFCC');
    const location = useLocation();
    const navigateTo = useNavigateTo();
    const searchUrl: string = useSearchUrl();
    const { buildSearchProductURL } = useBuildURL();
    const storesFindUrl = useUrl(PageUrl.STORES_FIND);
    const storesUrl = enableRioSEO ? PageUrl.STORES : storesFindUrl;
    const contactUrl = useUrl(PageUrl.CUSTOMER_CONTACT_US);
    const [current, setCurrent] = useState(data ?? []);
    const [selected, setSelected] = useState<Categories>([]);
    const [isSearch, setIsSearch] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
    const [level, setLevel] = useState(CategoryLevel.ONE);
    const [bloomreachParams, setBloomreachParams] = useState<{ url: string; refUrl: string }>({
        url: '',
        refUrl: '',
    });

    const { data: search, isLoading } = useGetBloomreachSearch({
        ...bloomreachParams,
        q: debouncedSearchTerm,
        pageSize: 4,
    });
    const brands = useGetSuggestedBrands({
        searchTerm: debouncedSearchTerm,
    });

    useEffect(() => {
        if (isHydrated() && window.document.URL) {
            setBloomreachParams({
                url: window.document.URL,
                refUrl: window.document.referrer || '',
            });
        }
    }, []);

    useEffect(() => {
        setIsSearch(!isLoading && shouldQueryBloomreach(debouncedSearchTerm));
    }, [isLoading, debouncedSearchTerm]);

    useEffect(() => {
        if (data) {
            setCurrent(data);
        }
    }, [data]);

    const firstSelected = selected.length ? selected[0] : data?.[0];
    const lastSelected = selected.length ? selected[selected.length - 1] : null;

    const onCloseDrawer = (): void => {
        onClose();
        setCurrent(data ?? []);
        setSelected([]);
        setLevel(CategoryLevel.ONE);
        setSearchTerm('');
        setIsSearch(false);
    };

    const onSelect = (category: Category): void => {
        setLevel(level + 1);
        setSelected(selected.concat(category));
        setCurrent(category.subCategories);
    };

    const onBack = (): void => {
        setLevel(level - 1);
        selected.pop();
        setSelected(selected);

        if (CategoryLevel.TWO >= level) {
            setCurrent(data ?? []);
        } else {
            setCurrent(selected.length ? selected[0].subCategories : []);
        }
    };

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e): void => {
        if (e.key === 'Enter' && searchTerm) {
            e.preventDefault();

            onCloseDrawer();

            if (location.pathname.startsWith(searchUrl) && pwaSearchMode !== 'SFCC') {
                dispatchEvent(new CustomEvent('slt-search', { detail: searchTerm }));
            } else {
                navigateTo(buildSearchProductURL(searchTerm), {
                    useWindowLocation: pwaSearchMode === 'SFCC',
                });
            }
        }
    };

    const onSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(e.target.value);
    };

    const onClearSearch = (): void => {
        setSearchTerm('');
    };

    const isLevel1 = CategoryLevel.ONE === level;

    return (
        <Drawer placement="left" isOpen={isOpen} onClose={onCloseDrawer}>
            <DrawerOverlay />
            <DrawerContent
                data-testid="header.menu.mobile.category"
                maxHeight="100%"
                maxWidth={{ xs: '300px', sm: '75%' }}
                padding={0}
                borderRadius={0}
            >
                <DrawerHeader data-testid="header.menu.mobile.category.header" padding={4}>
                    <Flex direction="column" gap={4}>
                        <Flex alignItems="center" justifyContent="space-between" height={8}>
                            {isLevel1 ? (
                                <BrandLogo
                                    data-testid="header.menu.category.logo"
                                    width={140}
                                    height="31.5px"
                                />
                            ) : (
                                <>
                                    <ArrowLeftIcon
                                        data-testid="header.menu.mobile.category.back.button"
                                        onClick={onBack}
                                        cursor="pointer"
                                    />

                                    <Text id="header.menu.category.title">
                                        {lastSelected!.displayName.toUpperCase()}
                                    </Text>
                                </>
                            )}

                            <DrawerCloseButton
                                data-testid="header.menu.mobile.category.close.button"
                                size="lg"
                                display="contents"
                                paddingTop={isLevel1 ? 2 : 0}
                                paddingBottom={isLevel1 ? 0 : 2}
                            />
                        </Flex>

                        <InputGroup>
                            <Input
                                variant="slt-search"
                                data-testid="header.menu.mobile.category.search.input"
                                type="search"
                                autoComplete="off"
                                onKeyDown={onKeyDown}
                                onChange={onSearch}
                                value={searchTerm}
                                placeholder={intl.formatMessage({
                                    id: 'header.menu.category.search.placeholder',
                                    defaultMessage: 'Search',
                                })}
                            />
                            <InputRightElement>
                                {searchTerm ? (
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center"
                                        data-testid="header.menu.mobile.category.search.clear.button"
                                        onClick={onClearSearch}
                                    >
                                        <CircleCloseIcon data-testid="header.menu.mobile.category.clear.search.icon" />
                                    </button>
                                ) : (
                                    <SearchIcon
                                        data-testid="header.menu.mobile.category.search.icon"
                                        width={4}
                                        height={4}
                                    />
                                )}
                            </InputRightElement>
                        </InputGroup>
                    </Flex>
                </DrawerHeader>

                <DrawerBody data-testid="header.menu.mobile.category.body" padding={0}>
                    {isSearch ? (
                        <Flex direction="column" rowGap={8}>
                            <MenuSearchResults
                                searchTerm={debouncedSearchTerm}
                                phrases={search?.phrases ?? []}
                                categories={search?.categories ?? []}
                                brands={brands?.data?.brands ?? []}
                                hardgood={search?.hardgood ?? []}
                                culinary={search?.culinary ?? []}
                                recipe={search?.recipe ?? []}
                                onClickResult={onClose}
                                onClosePopover={onClose}
                            />
                        </Flex>
                    ) : (
                        <Flex direction="column">
                            {firstSelected?.showShopAllLink ? (
                                <Link
                                    href={firstSelected.url}
                                    data-testid={`header.menu.mobile.category.${firstSelected.ID}.link`}
                                    className="w-fit px-4 pb-3 underline"
                                >
                                    <Text>
                                        <FormattedMessage
                                            id="header.shop.all"
                                            defaultMessage="Shop All {category}"
                                            values={{
                                                category: firstSelected.displayName,
                                            }}
                                        />
                                    </Text>
                                </Link>
                            ) : null}

                            {CategoryLevel.TWO >= level ? (
                                <MenuCategoryMobileLevel12
                                    categories={current}
                                    onSelect={onSelect}
                                />
                            ) : null}

                            {CategoryLevel.THREE === level ? (
                                <MenuCategoryMobileLevel34
                                    categories={current}
                                    lastSelected={lastSelected}
                                />
                            ) : null}

                            {firstSelected && isBrandsCategory(firstSelected) ? (
                                <MenuCategoryBrandsMobile />
                            ) : null}

                            {firstSelected?.headerMenuBanner1 ? (
                                <BannerLayout
                                    id={`header.menu.mobile.category.lvl1.${firstSelected.ID}.banner1`}
                                    banner={firstSelected?.headerMenuBanner1}
                                />
                            ) : null}

                            {firstSelected?.headerMenuBanner2 ? (
                                <BannerLayout
                                    id={`header.menu.mobile.category.lvl1.${firstSelected.ID}.banner2`}
                                    banner={firstSelected?.headerMenuBanner2}
                                />
                            ) : null}

                            {firstSelected?.headerMenuBanner3 ? (
                                <BannerLayout
                                    id={`header.menu.mobile.category.lvl1.${firstSelected.ID}.banner3`}
                                    banner={firstSelected?.headerMenuBanner3}
                                />
                            ) : null}

                            {firstSelected?.headerMenuBanner4 ? (
                                <BannerLayout
                                    id={`header.menu.mobile.category.lvl1.${firstSelected.ID}.banner4`}
                                    banner={firstSelected?.headerMenuBanner4}
                                />
                            ) : null}
                        </Flex>
                    )}
                </DrawerBody>

                <DrawerFooter
                    data-testid="header.menu.mobile.category.footer"
                    background="slt-black.50"
                    justifyContent="center"
                    boxShadow="none"
                    padding={4}
                >
                    <Flex
                        grow={1}
                        align="center"
                        justify="space-between"
                        maxWidth={{ base: '100%', sm: '75%' }}
                    >
                        {isInitialized ? (
                            <Link
                                data-testid="header.menu.mobile.category.footer.customer.link"
                                href={accountUrl}
                            >
                                <Flex alignItems="center" cursor="pointer">
                                    <UserIcon
                                        data-testid="header.menu.mobile.category.footer.customer.icon"
                                        margin={1}
                                        width={3}
                                        height={3}
                                    />
                                    <Text
                                        id="header.menu.mobile.category.footer.customer"
                                        size="small"
                                        style="bolder"
                                    >
                                        {isRegistered && customer?.firstName ? (
                                            customer?.firstName
                                        ) : (
                                            <FormattedMessage
                                                id="header.customer"
                                                defaultMessage="Sign In"
                                            />
                                        )}
                                    </Text>
                                </Flex>
                            </Link>
                        ) : null}

                        <Link
                            data-testid="header.menu.mobile.category.footer.stores.link"
                            href={storesUrl}
                        >
                            <Flex alignItems="center" cursor="pointer">
                                <StoreIcon
                                    data-testid="header.menu.mobile.category.footer.stores.icon"
                                    margin={1}
                                    width={3}
                                    height={3}
                                />
                                <Text
                                    id="header.menu.mobile.category.footer.stores"
                                    size="small"
                                    style="bolder"
                                >
                                    <FormattedMessage id="header.stores" defaultMessage="Stores" />
                                </Text>
                            </Flex>
                        </Link>

                        <Link
                            data-testid="header.menu.mobile.category.footer.contact.link"
                            href={contactUrl}
                        >
                            <Flex alignItems="center" cursor="pointer">
                                <ContactIcon
                                    data-testid="header.menu.mobile.category.footer.contact.icon"
                                    margin={1}
                                    width={3}
                                    height={3}
                                />
                                <Text
                                    id="header.menu.mobile.category.footer.contact"
                                    size="small"
                                    style="bolder"
                                >
                                    <FormattedMessage
                                        id="header.contact"
                                        defaultMessage="Contact"
                                    />
                                </Text>
                            </Flex>
                        </Link>
                    </Flex>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};
